import { computeLineWeight } from './weight-calculator.js';
/**
 * Walk the segment tree bottom-up, computing line weights and rolling
 * them up into segment totals (rebarWeight, plainWeight, miscWeight, totalWeight).
 *
 * Mutates segments and their lines in place.
 * Alternate-bid segments are computed but excluded from parent totals.
 *
 * `defaults` is optional; when supplied it's forwarded to `computeLineWeight`
 * so tie weight calculations can honor `defaults.seismicHooks`. Callers that
 * don't pass defaults get the legacy 90° standard-hook behavior.
 */
export function rollupSegments(segments, barMarks, defaults) {
    // Build parent → children map
    const childrenOf = new Map();
    for (const seg of segments) {
        if (seg.parentSegmentId) {
            const siblings = childrenOf.get(seg.parentSegmentId) ?? [];
            siblings.push(seg);
            childrenOf.set(seg.parentSegmentId, siblings);
        }
    }
    // Recursive rollup
    function rollup(segment) {
        // First, rollup all children
        const children = childrenOf.get(segment.segmentId) ?? [];
        for (const child of children) {
            rollup(child);
        }
        // Compute line weights for this segment, bucketing by material:
        //   TR (rebar) → rebarWeight
        //   TP (plain steel) → plainWeight
        //   TI (mesh sheets) → miscWeight
        //   GI / MT (informational) → no contribution (computeLineWeight returns 0)
        let rebarWeight = 0;
        let plainWeight = 0;
        let miscWeight = 0;
        for (const line of segment.lines) {
            // Recompute only TR weights from formulas. TP (plain steel) and TI
            // (mesh) carry authored weight values from the source aSa estimate
            // — preserve them rather than overwrite with the rebar-only
            // computeLineWeight (which returns 0 for non-TR material).
            if (line.material === 'TR') {
                line.weightLbs = computeLineWeight(line, barMarks, defaults);
            }
            switch (line.material) {
                case 'TR':
                    rebarWeight += line.weightLbs;
                    break;
                case 'TP':
                    plainWeight += line.weightLbs;
                    break;
                case 'TI':
                    miscWeight += line.weightLbs;
                    break;
                // GI / MT contribute 0 weight — no bucket needed
            }
        }
        // Add non-alternate children's totals into the matching parent buckets
        for (const child of children) {
            if (!child.alternateBid) {
                rebarWeight += child.rebarWeight;
                plainWeight += child.plainWeight;
                miscWeight += child.miscWeight;
            }
        }
        segment.rebarWeight = rebarWeight;
        segment.plainWeight = plainWeight;
        segment.miscWeight = miscWeight;
        segment.totalWeight = rebarWeight + plainWeight + miscWeight;
    }
    // Start from root segments (no parent)
    const roots = segments.filter(s => s.parentSegmentId === null);
    for (const root of roots) {
        rollup(root);
    }
}
